import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../translations';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMobileMenuOpen(false);
  };

  const goToHome = () => {
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goToPricing = () => {
    navigate('/pricing');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsMobileMenuOpen(false);
  };

  const t = (key) => getTranslation(language, key);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-slate-950/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex-shrink-0">
            <button
              onClick={goToHome}
              className="flex items-center hover:opacity-80 transition-opacity duration-300"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_brand-showcase-98/artifacts/1mp62km8_wwi%20logo.png"
                alt="Worldwide Influence Media"
                className="h-20 sm:h-24 md:h-28 lg:h-32 w-auto"
              />
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => scrollToSection('services')}
              className="text-gray-300 hover:text-white transition-colors duration-300 text-sm font-medium"
            >
              {t('nav.services')}
            </button>
            <button
              onClick={() => scrollToSection('clients')}
              className="text-gray-300 hover:text-white transition-colors duration-300 text-sm font-medium"
            >
              {t('nav.clients')}
            </button>
            <button
              onClick={goToPricing}
              className="text-gray-300 hover:text-white transition-colors duration-300 text-sm font-medium"
            >
              Pricing
            </button>
            <button
              onClick={() => navigate('/about')}
              className="text-gray-300 hover:text-white transition-colors duration-300 text-sm font-medium"
            >
              About
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="text-gray-300 hover:text-white transition-colors duration-300 text-sm font-medium"
            >
              {t('nav.contact')}
            </button>

            <Button
              onClick={() => scrollToSection('contact')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 transition-colors duration-300"
            >
              {t('nav.getStarted')}
            </Button>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-white p-2"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-800">
            <nav className="flex flex-col space-y-4">
              <button
                onClick={() => scrollToSection('services')}
                className="text-gray-300 hover:text-white transition-colors duration-300 text-left"
              >
                {t('nav.services')}
              </button>
              <button
                onClick={() => scrollToSection('clients')}
                className="text-gray-300 hover:text-white transition-colors duration-300 text-left"
              >
                {t('nav.clients')}
              </button>
              <button
                onClick={goToPricing}
                className="text-gray-300 hover:text-white transition-colors duration-300 text-left"
              >
                Pricing
              </button>
              <button
                onClick={() => { navigate('/about'); setIsMobileMenuOpen(false); }}
                className="text-gray-300 hover:text-white transition-colors duration-300 text-left"
              >
                About
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="text-gray-300 hover:text-white transition-colors duration-300 text-left"
              >
                {t('nav.contact')}
              </button>

              <Button
                onClick={() => scrollToSection('contact')}
                className="bg-blue-600 hover:bg-blue-700 text-white w-full"
              >
                {t('nav.getStarted')}
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
